<template>
  <section class="TheReferralPolicy">
    <h2 class="TheReferralPolicy_Heading">
      Grove Collaborative Referral Program Policy
    </h2>
    <p class="TheReferralPolicy_EffectiveDate">
      Effective Date: January 1, 2020
    </p>

    <PoliciesGrovePronounDisclaimer />

    <p>
      This referral program policy is incorporated into and is subject to
      <SkyLink inline to="/terms">the Grove Collaborative Terms of Use</SkyLink
      >. Capitalized terms used in this referral program policy but not defined
      have the meaning set forth in
      <SkyLink inline to="/terms">the Grove Collaborative Terms of Use</SkyLink
      >.
    </p>

    <p>
      Subject to this referral program policy, Grove Collaborative may offer you
      the ability to earn credits or discounts (<strong
        >“Referral Credits”</strong
      >) toward future shipments of products from Grove Collaborative when you
      invite friends to purchase from the Service (the
      <strong>“Referral Program”</strong>).
    </p>

    <h3 class="TheReferralPolicy_Subheading">Referral Program Details</h3>

    <p>
      You may invite friends, family members, or colleagues whom you personally
      know (“Friends”) to purchase from the Service. You will earn Referral
      Credits for Friends who are not existing customers of the Grove
      Collaborative Service and who are first-time customers as a result of your
      Authorized Invitation (as defined below). You may refer a maximum of 50
      Friends throughout the entirety of the Referral Program, but we reserve
      the right to increase or decrease that maximum for certain partners in our
      sole discretion as authorized in writing.
    </p>

    <p>
      You must include your personal unique referral link (“Personal Link”) in
      your invitation, or you must invite your Friends through one of the
      invitation mechanisms that we authorize through our Service or on our
      social media platforms (each, an
      <strong>“Authorized Invitation”</strong>). You will not earn Referral
      Credits based on invitations that are not Authorized Invitations.
    </p>

    <p>
      You will not be eligible for Referral Credits unless you have established
      an active account on the Service and placed an order before your Friend
      uses your Authorized Invitation to make a purchase. You will only receive
      Referral Credits for each of your Friends’ first qualifying purchase (as
      described in the Authorized Invitation or elsewhere on the Service) made
      as a result of your Authorized Invitation within six (6) months from the
      date that you sent the Authorized Invitation. If your friend does not
      follow the directions in the Authorized Invitation, you may not receive
      Referral Credit.
    </p>

    <p>
      Referral Credits are not transferable, may not be auctioned, bartered or
      sold, may not be pooled with other members’, and expire six (6) months
      after issuance.
    </p>

    <p>
      Referral Credits will appear in your account within approximately 24 hours
      after one of your friends makes their qualifying purchase and we ship
      their purchased products. Referral Credits may only be used on grove.co
      and may not be used on any other website.
    </p>

    <h4 class="TheReferralPolicy_SectionHeading">Restrictions</h4>
    <p>
      You may not refer yourself or create multiple, fictitious or fake accounts
      with Grove Collaborative or any social media service that we determine in
      our sole discretion violates the spirit and intent of our Referral
      Program. You may not (i) tamper with the Referral Program, (ii) act in an
      unfair or disruptive manner, or (iii) use any system, bot or other device
      or artifice to participate or receive any benefit in the Referral Program.
      This includes but is not limited to a general prohibition on posting of
      your Personal Link or any other referral codes on any website or social
      media platform which offers the referral code to anyone other than Friends
      if we determine in our sole discretion that your posting circumvents the
      spirit and intent of the Referral Program. You may not post on public
      websites “recipes” designed to instruct the general public how to commit
      fraud, increase your Referral Credits, or circumvent the spirit and intent
      of the Referral Program. You may not share any referral code other than
      your Personal Link or another then-current authorized referral code. You
      may not conduct your own promotion in connection with our Referral
      Program. You may not engage in any promotional, marketing, or other
      advertising activities on behalf of Grove Collaborative or any other third
      parties, including by using any trademarks of Grove Collaborative or other
      third parties.
    </p>

    <p>
      You may not use the Program to violate any law, infringe or violate the
      rights of any third party, or otherwise act in a manner that is deemed
      harassing, harmful, illegal, hateful, obscene or outside the spirit and
      intent of the Referral Program.
    </p>

    <p>
      We reserve the right to void referrals and Referral Credits if we suspect
      that the referrals or Referral Credits were earned in a fraudulent manner,
      in a manner that violates this policy or
      <SkyLink inline to="/terms">
        the Grove Collaborative Terms and Conditions </SkyLink
      >, or in a manner otherwise not intended by Grove Collaborative. If we
      believe that a referral code associated with your account was used in such
      a manner, we have the right to remove all Referral Credits associated with
      that account and remove you from the Service.
    </p>

    <p>
      The Referral Program is void where prohibited by law. Grove Collaborative
      reserves the right to modify or terminate the Referral Program at any
      time, although (subject this refund and referral policy) Referral Credits
      accrued shall be valid for six (6) months from the date of posting. Should
      there be any tax liability for the accumulation and/or use of Referral
      Credits, such taxes are your sole responsibility. You understand that your
      account may not accurately reflect the Referral Credits you have actually
      earned. Grove Collaborative will have no liability for any errors
      displayed in your account with respect to Referral Credits. If you have
      any questions regarding the Referral Program, please contact us at
      <SkyLink inline to="mailto:support@grove.co" tag="a"
        >support@grove.co</SkyLink
      >.
    </p>
  </section>
</template>

<style lang="scss">
.TheReferralPolicy {
  display: grid;
  row-gap: var(--spacing-3x);

  &_Heading {
    @include type-brand-heading('2');
    margin-top: var(--spacing-8x);
  }

  &_EffectiveDate {
    @include type-body('l', true);
    margin-bottom: var(--spacing-3x);
  }

  &_Subheading {
    @include type-brand-heading('3');
    margin-top: var(--spacing-6x);
  }

  &_SectionHeading {
    @include type-brand-heading('4');
    margin-top: var(--spacing-6x);
  }
}
</style>
